import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { SERVICES, CREATORS, COMPANY } from "@/data/content";

export default function Creators() {
  return (
    <div data-testid="creators-page">
      <PageHeader
        kicker="Working with creators"
        title='Selective. Long-term. <span class="text-warm-600">Relationship-led.</span>'
        intro="LENNON MANAGEMENT works deeply with a small number of creators — on career, partnerships, business development and long-term commercial strategy. We are not a roster, a marketplace, or a campaign platform."
      />

      {/* PHILOSOPHY PULL QUOTE */}
      <section className="bg-ink text-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14">
          <Reveal>
            <div className="eyebrow text-warm-400 mb-10">Philosophy</div>
            <h2
              className="display text-paper"
              style={{ fontSize: "clamp(36px, 6vw, 112px)", lineHeight: 0.96, letterSpacing: "-0.04em" }}
            >
              Creators aren't<br />
              media buys.<br />
              <span className="text-warm-400">They are culture.</span>
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="mt-14 md:mt-20 max-w-3xl text-warm-400 text-lg md:text-xl leading-relaxed">
              {COMPANY.philosophy}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOUR PILLARS */}
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
            <div className="md:col-span-7">
              <div className="eyebrow mb-4">What we do</div>
              <h2 className="display text-ink" style={{ fontSize: "clamp(40px, 6vw, 96px)", letterSpacing: "-0.03em" }}>
                Three practices.<br />One relationship.
              </h2>
            </div>
            <p className="md:col-span-5 text-warm-600 text-base md:text-lg leading-relaxed">
              We work with creators over years, not campaigns. What starts as
              management usually becomes a business — books, products, IP, new
              companies.
            </p>
          </div>
        </Reveal>

        <ol className="border-t border-line">
          {SERVICES.map((s, i) => (
            <Reveal as="li" key={s.n} delay={i * 80}>
              <article
                data-testid={`service-${s.n}`}
                className="grid grid-cols-12 gap-6 md:gap-10 border-b border-line py-12 md:py-20"
              >
                <div className="col-span-12 md:col-span-2 eyebrow text-warm-600 pt-3">
                  — {s.n}
                </div>
                <div className="col-span-12 md:col-span-5">
                  <h3
                    className="display text-ink"
                    style={{ fontSize: "clamp(32px, 4.5vw, 72px)", letterSpacing: "-0.02em" }}
                  >
                    {s.t}
                  </h3>
                  <p className="mt-6 text-ink-800 text-lg md:text-xl leading-snug max-w-xl">
                    {s.lead}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-5 md:pt-4">
                  <ul className="space-y-4">
                    {s.items.map((item, j) => (
                      <li
                        key={j}
                        className="flex items-baseline gap-4 border-b border-line/60 pb-4 last:border-b-0"
                      >
                        <span className="eyebrow text-warm-600 shrink-0 w-10">
                          {String(j + 1).padStart(2, "0")}
                        </span>
                        <span className="text-ink text-base md:text-lg leading-snug">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* ROSTER / SELECT CREATORS */}
      <section data-testid="creators-roster" className="border-t border-line bg-mute py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
              <div className="md:col-span-7">
                <div className="eyebrow mb-4">Selected creators</div>
                <h2 className="display text-ink" style={{ fontSize: "clamp(40px, 6vw, 96px)", letterSpacing: "-0.03em" }}>
                  A history of<br />creator businesses.
                </h2>
              </div>
              <p className="md:col-span-5 text-warm-600 text-base md:text-lg leading-relaxed">
                A selection of creators represented across fifteen years of
                Lennon Management. Some remain active. All shaped what came next.
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
              {CREATORS.map((c) => (
                <li
                  key={c.handle}
                  data-testid={`creator-${c.handle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="bg-paper p-8 md:p-12"
                >
                  <div className="eyebrow text-warm-600 mb-3">{c.handle}</div>
                  <div className="display text-ink" style={{ fontSize: "clamp(24px, 2.6vw, 40px)", letterSpacing: "-0.02em" }}>
                    {c.name}
                  </div>
                  <div className="mt-3 text-warm-600 text-sm md:text-base">
                    {c.note}
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-t border-line">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="eyebrow mb-4">Working together</div>
              <h2
                className="display text-ink"
                style={{ fontSize: "clamp(40px, 6vw, 96px)", letterSpacing: "-0.03em" }}
              >
                Building something<br />with a creator?
              </h2>
              <p className="mt-6 text-warm-600 text-base md:text-lg max-w-xl leading-relaxed">
                Whether you&rsquo;re a creator looking for long-term representation,
                or a brand or publisher looking to build a meaningful creator
                partnership — start the conversation.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end gap-6 items-center flex-wrap">
              <Link
                to="/contact"
                data-testid="creators-cta-contact"
                className="inline-flex items-center gap-3 bg-ink text-paper px-7 py-4 text-sm font-medium hover:bg-ink-800 transition-colors"
              >
                Get in touch <ArrowRight size={16} />
              </Link>
              <a
                href={COMPANY.companiesUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="creators-cta-companies"
                className="inline-flex items-center gap-2 text-sm text-ink hover:text-warm-600 border-b border-ink/40 hover:border-warm-600 pb-1"
              >
                For companies · Lennon Global <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
